import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, CaciDetailStyles, PageHeader, SectionNote, Seal,
  Prose, ShareBar, FileList, DetailSkeleton,
  useReveal, useScrollProgress,
  stripHtml, truncate, langPath, localized,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   PAGEDETAIL
   ───────────────────────────────────────────────────────────────────────────
   Same endpoints (/api/pages/edit/:id and /api/serviceportail/list_page),
   same attachments and service sidebar. It shares the detail layout with
   ArticleDetail, so the same fixes apply here: real prose typography, one
   share row instead of two, a not-found panel instead of navigate("*") during
   render, skeletons instead of a blank screen, a language-aware sidebar
   search, and guarded slugs so a missing one no longer produces
   "/service/12/undefined".
   ═══════════════════════════════════════════════════════════════════════════ */

function PageDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [page, setPage] = useState(null);
  const [services, setServices] = useState([]);
  const [state, setState] = useState("loading");
  const [term, setTerm] = useState("");

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(() => {
    setState("loading");

    const main = axios
      .get(`${BASE_URL}/api/pages/edit/${id}`)
      .then((res) => {
        if (!alive.current) return;
        if (!res.data || !res.data.id) { setState("missing"); return; }
        setPage(res.data);
        setState("ready");
      })
      .catch((err) => {
        if (!alive.current) return;
        console.error(`PageDetail: page ${id} failed to load —`, err);
        setState(err?.response?.status === 404 ? "missing" : "error");
      });

    /* Independent: a broken service list must not take the page down with it. */
    axios
      .get(`${BASE_URL}/api/serviceportail/list_page`)
      .then((res) => {
        if (!alive.current) return;
        setServices(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error("PageDetail: service list failed to load —", err));

    return main;
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [id]);

  const pageRef = useReveal([page, state, services]);
  const progress = useScrollProgress();

  const title = page ? stripHtml(localized(page, "name", lang)) : "";
  const bodyHtml = page ? localized(page, "description", lang) : "";
  const summary = truncate(stripHtml(bodyHtml), 180);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareImage = page?.image
    ? (String(page.image).startsWith("http") ? page.image : `${BASE_URL}${page.image}`)
    : "";

  const serviceLink = (s) => (s?.slug ? `/service/${s.id}/${s.slug}` : `/service/${s.id}`);

  const files = (page?.file_pages || []).filter(
    (f) => f && !f.deleted_at && (!f.lang || f.lang === lang)
  );

  const onSearch = (e) => {
    e.preventDefault();
    const q = term.trim();
    if (!q) return;
    navigate(`${langPath(lang, "/search")}?keyword=${encodeURIComponent(q)}`);
  };

  const listLabel = t("menu.Toutpage", "Pages");

  if (state === "missing" || state === "error") {
    const missing = state === "missing";
    return (
      <div className={`caci-page${isRtl ? " is-rtl" : ""}`} dir={isRtl ? "rtl" : "ltr"}>
        <CaciStyles />
        <Helmet>
          <title>{missing ? t("notfound.title", "Introuvable") : t("search.errorTitle", "Erreur")} — {t("menu.caci")}</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <PageHeader
          title={missing ? t("notfound.title", "Page introuvable") : t("search.errorTitle", "Chargement impossible")}
          crumbs={[
            { label: t("menu.home"), to: langPath(lang, "/") },
            { label: listLabel, to: langPath(lang, "/toutpage") },
          ]}
        />
        <section className="caci-section">
          <div className="caci-shell">
            <SectionNote
              state={missing ? "empty" : "error"}
              title={missing
                ? t("notfound.heading_page", "Cette page n'existe pas ou a été retirée.")
                : t("home.section_error", "Cette page n'a pas pu être chargée.")}
              action={
                missing ? (
                  <button type="button" className="btn-quiet" onClick={() => navigate(langPath(lang, "/toutpage"))}>
                    <i className={`bi bi-arrow-${isRtl ? "right" : "left"}`} />
                    {listLabel}
                  </button>
                ) : (
                  <button type="button" className="btn-quiet" onClick={load}>
                    <i className="bi bi-arrow-clockwise" />
                    {t("home.retry", "Réessayer")}
                  </button>
                )
              }
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />
      <CaciDetailStyles />

      {state === "ready" && (
        <Helmet>
          <title>{title} — {t("menu.caci")}</title>
          <meta name="description" content={summary} />
          {page?.service && (
            <meta name="keywords" content={stripHtml(localized(page.service, "name", lang))} />
          )}
          <link rel="canonical" href={shareUrl} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={summary} />
          {shareImage && <meta property="og:image" content={shareImage} />}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:site_name" content={t("menu.caci")} />
          <meta name="twitter:card" content={shareImage ? "summary_large_image" : "summary"} />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={summary} />
          {shareImage && <meta name="twitter:image" content={shareImage} />}
        </Helmet>
      )}

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={state === "ready" ? title : listLabel}
        crumbs={[
          { label: t("menu.home"), to: langPath(lang, "/") },
          { label: listLabel, to: langPath(lang, "/toutpage") },
          { label: state === "ready" ? truncate(title, 40) : listLabel },
        ]}
      />

      <section className="caci-section">
        <div className="caci-shell">
          <div className="detail-wrap">
            <div className="detail-main">
              {state === "loading" && <DetailSkeleton />}

              {state === "ready" && (
                <article>
                  {page.image && (
                    <figure className="detail-hero" data-rv="zoom">
                      <img
                        src={`${BASE_URL}${page.image}`}
                        alt="" loading="eager" decoding="async"
                        onError={(e) => { e.currentTarget.closest(".detail-hero").style.display = "none"; }}
                      />
                    </figure>
                  )}

                  {page.service && (
                    <div className="detail-meta" data-rv="fade">
                      <span>
                        <i className="bi bi-briefcase" />
                        {truncate(stripHtml(localized(page.service, "name", lang)), 42)}
                      </span>
                    </div>
                  )}

                  <Prose html={bodyHtml} />

                  {page.service && (
                    <div className="detail-tags">
                      <span className="detail-tags-label">
                        <i className="bi bi-diagram-3" />
                        {t("menu.Services", "Services")}
                      </span>
                      <Link to={serviceLink(page.service)} className="art-cat">
                        {truncate(stripHtml(localized(page.service, "name", lang)), 34)}
                      </Link>
                    </div>
                  )}

                  <ShareBar
                    url={shareUrl}
                    title={title}
                    label={t("menu.Share")}
                    nativeLabel={t("menu.Share")}
                    copiedLabel={t("share.copied", "Lien copié")}
                  />
                </article>
              )}
            </div>

            <aside className="side">
              <div className="side-card">
                <h2 className="side-title"><Seal size={13} />{t("menu.Search")}</h2>
                <form onSubmit={onSearch}>
                  <div className="field">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input
                      type="search" value={term} onChange={(e) => setTerm(e.target.value)}
                      placeholder={t("search.placeholder")} aria-label={t("search.placeholder")} dir="auto"
                    />
                  </div>
                </form>
              </div>

              {files.length > 0 && (
                <div className="side-card">
                  <h2 className="side-title"><Seal size={13} />{t("home.Download_Catalog")}</h2>
                  <FileList files={files} baseUrl={BASE_URL} />
                </div>
              )}

              {services.length > 0 && (
                <div className="side-card">
                  <h2 className="side-title"><Seal size={13} />{t("menu.Services", "Services")}</h2>
                  <ul className="side-list">
                    {services.map((s, i) => (
                      <li key={s.id ?? i}>
                        <Link to={serviceLink(s)}>
                          <span dir="auto">{truncate(stripHtml(localized(s, "name", lang)), 40)}</span>
                          {typeof s.pages_count === "number" && (
                            <span className="side-count">{s.pages_count}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PageDetail;
